import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Clock, Gavel, Eye, ShoppingBag, ArrowLeftRight, Share2, Flag, Pencil, Lock } from 'lucide-react';
import { formatCurrency, getTimeRemaining, getConditionLabel, truncateText, cn, getThumbnailUrl, getFullImageUrl } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import LikeButton from '@/components/listings/LikeButton';

// Seller Avatar component with proper image fallback
const SellerAvatar = ({ avatar, name }) => {
    const [imgError, setImgError] = useState(false);
    const initial = name?.[0]?.toUpperCase() || '?';
    
    if (avatar && !imgError) {
        return (
            <div className="w-6 h-6 rounded-full bg-primary overflow-hidden flex-shrink-0">
                <img 
                    src={getFullImageUrl(avatar)}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                />
            </div>
        );
    }
    
    return (
        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
            {initial}
        </div>
    );
};

export const ListingCard = ({ listing, onWatchlistToggle, isWatched = false, onReport, singleColumn = false }) => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const timeRemaining = listing.end_date ? getTimeRemaining(listing.end_date) : null;
    const isAuction = listing.listing_type === 'auction';
    const isOwner = user?.user_id === listing.seller_id;
    
    // Use slug if available, otherwise fallback to listing_id
    const listingUrl = `/listing/${listing.slug || listing.listing_id}`;
    
    // Use small thumbnail (300px) - good enough for card display on all devices
    // This saves bandwidth while still looking sharp
    const imageUrl = listing.images?.[0];
    const thumbnailUrl = imageUrl ? getThumbnailUrl(imageUrl, 300, 75) : null;
    const defaultImage = 'https://images.unsplash.com/photo-1680994330269-993e90deeca3?w=300';

    const handleEdit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/dashboard?tab=for-sale&edit=${listing.listing_id}`);
    };

    const handleShare = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const shareUrl = `https://www.colectarium.ro/listing/${listing.slug || listing.listing_id}`;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: listing.title,
                    text: listing.description?.substring(0, 100) || 'Vezi acest anunț pe Colectarium',
                    url: shareUrl
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    navigator.clipboard.writeText(shareUrl);
                    toast.success('Link copiat în clipboard!');
                }
            }
        } else {
            navigator.clipboard.writeText(shareUrl);
            toast.success('Link copiat în clipboard!');
        }
    };

    const handleReport = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onReport) {
            onReport(listing);
        } else {
            // Navigate to listing page with report modal query
            window.location.href = `/listing/${listing.slug || listing.listing_id}?report=true`;
        }
    };

    // Single column horizontal layout for mobile
    if (singleColumn) {
        return (
            <Card 
                className="group bg-white rounded-2xl border border-border/50 overflow-hidden hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300"
                data-testid={`listing-card-${listing.listing_id}`}
            >
                <div className="flex h-40">
                    {/* Left side - Thumbnail (50% width, full height, rounded right corners) */}
                    <Link to={listingUrl} className="flex-shrink-0 w-1/2 h-full relative bg-gray-50">
                        <img
                            src={thumbnailUrl || defaultImage}
                            alt={listing.title}
                            className="w-full h-full object-contain rounded-r-xl"
                            loading="lazy"
                            decoding="async"
                        />
                    </Link>
                    
                    {/* Right side - Content */}
                    <div className="flex-1 p-3 flex flex-col min-w-0">
                        {/* Row 1: Title */}
                        <Link to={listingUrl} className="block">
                            <h3 className="font-medium text-base text-foreground line-clamp-2 group-hover:text-primary transition-colors leading-tight">
                                {listing.title}
                            </h3>
                        </Link>
                        
                        {/* Row 2: Category & Subcategory */}
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-2 flex-wrap">
                            {listing.category_name && (
                                <Link 
                                    to={`/category/${listing.category_slug || listing.category_id}`}
                                    className="hover:text-primary transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {listing.category_name}
                                </Link>
                            )}
                            {listing.subcategory_name && (listing.subcategory_slug || listing.subcategory_id) && (
                                <>
                                    <span>/</span>
                                    <Link 
                                        to={`/category/${listing.category_slug || listing.category_id}/${listing.subcategory_slug || listing.subcategory_id}`}
                                        className="hover:text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {listing.subcategory_name}
                                    </Link>
                                </>
                            )}
                        </div>
                        
                        {/* Spacer */}
                        <div className="flex-1"></div>
                        
                        {/* Row 3: Seller - right aligned */}
                        <div className="text-right">
                            <Link 
                                to={`/user/${listing.seller_username?.trim()}`}
                                className="text-[10px] text-muted-foreground hover:text-foreground transition-colors truncate inline-block mb-1"
                            >
                                {listing.seller_name || listing.seller_username}
                            </Link>
                        </div>
                        
                        {/* Row 3: Price - right aligned */}
                        <div className="text-right mb-1">
                            <Link to={listingUrl}>
                                <p className="font-heading font-bold text-base text-foreground leading-tight">
                                    {formatCurrency(listing.current_price)}
                                </p>
                            </Link>
                        </div>
                        
                        {/* Row 4: Bid count for auctions */}
                        {isAuction && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Gavel className="w-3 h-3" />
                                <span>{listing.bid_count || 0} {(listing.bid_count || 0) === 1 ? 'ofertă' : 'oferte'}</span>
                            </div>
                        )}
                        
                        {/* Row 5: Tags (left) & Actions (right) */}
                        <div className="flex items-end justify-between">
                            <div className="flex flex-col items-start gap-0.5">
                                <Badge 
                                    className={cn(
                                        "rounded-full font-medium text-[9px] px-1.5 py-0",
                                        isAuction 
                                            ? "bg-blue-500 text-white" 
                                            : "bg-green-500 text-white"
                                    )}
                                >
                                    {isAuction ? 'Licitație' : 'Preț fix'}
                                </Badge>
                                {listing.accepts_trades && (
                                    <Badge className="rounded-full font-medium text-[9px] px-1.5 py-0 bg-orange-500 text-white">
                                        Schimb
                                    </Badge>
                                )}
                                {listing.is_reserved && (
                                    <Badge className="rounded-full font-medium text-[9px] px-1.5 py-0 bg-yellow-500 text-black">
                                        Reținută
                                    </Badge>
                                )}
                            </div>
                            <div className="flex items-center shrink-0">
                                {isAuthenticated && !isOwner && (
                                    <button
                                        onClick={handleReport}
                                        className="w-6 h-6 rounded-full flex items-center justify-center text-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                                        title="Raportează"
                                    >
                                        <Flag className="w-3 h-3" />
                                    </button>
                                )}
                                <button
                                    onClick={handleShare}
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors"
                                    title="Distribuie"
                                >
                                    <Share2 className="w-3 h-3" />
                                </button>
                                <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                    <LikeButton 
                                        listingId={listing.listing_id}
                                        initialLikesCount={listing.likes_count || 0}
                                        size="xs"
                                        showCount={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card 
            className="group bg-white rounded-3xl border border-border/50 overflow-hidden hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
            data-testid={`listing-card-${listing.listing_id}`}
        >
            <Link to={listingUrl}>
                {/* Image - uses 300px thumbnail for all devices (bandwidth optimized) */}
                <div className="relative aspect-square overflow-hidden bg-white">
                    <img
                        src={thumbnailUrl || defaultImage}
                        alt={listing.title}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        decoding="async"
                    />
                    
                    {/* Type badge */}
                    <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
                        <Badge 
                            className={cn(
                                "rounded-full font-medium text-xs px-2 py-0.5 flex items-center",
                                isAuction 
                                    ? "bg-blue-500 text-white" 
                                    : "bg-green-500 text-white"
                            )}
                        >
                            {isAuction ? (
                                <>
                                    <Gavel className="w-3 h-3 mr-1" />
                                    Licitație
                                </>
                            ) : (
                                <>
                                    <ShoppingBag className="w-3 h-3 mr-1" />
                                    Preț fix
                                </>
                            )}
                        </Badge>
                        {listing.accepts_trades && (
                            <Badge 
                                className="rounded-full font-medium text-xs px-2 py-0.5 bg-orange-500 text-white flex items-center"
                                data-testid={`trade-badge-${listing.listing_id}`}
                            >
                                <ArrowLeftRight className="w-3 h-3 mr-1" />
                                Schimb
                            </Badge>
                        )}
                        {listing.is_reserved && (
                            <Badge 
                                className="rounded-full font-medium text-xs px-2 py-0.5 bg-yellow-500 text-black flex items-center"
                                data-testid={`reserved-badge-${listing.listing_id}`}
                            >
                                <Lock className="w-3 h-3 mr-1" />
                                Reținută
                            </Badge>
                        )}
                    </div>

                    {/* Time remaining for auctions */}
                    {isAuction && timeRemaining && !timeRemaining.expired && (
                        <div className={cn(
                            "absolute bottom-3 left-3 right-3 px-3 py-1.5 rounded-full text-xs font-medium flex items-center justify-center gap-1",
                            timeRemaining.urgent 
                                ? "bg-red-500 text-white" 
                                : "bg-white/90 text-foreground backdrop-blur-xs"
                        )}>
                            <Clock className="w-3 h-3" />
                            {timeRemaining.text}
                        </div>
                    )}
                </div>
            </Link>

            <CardContent className="p-4 flex flex-col flex-1">
                {/* Category & Subcategory - clickable, outside of main link */}
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    {listing.category_name && (
                        <Link 
                            to={`/category/${listing.category_slug || listing.category_id}`}
                            className="hover:text-primary transition-colors"
                        >
                            {listing.category_name}
                        </Link>
                    )}
                    {listing.subcategory_name && (listing.subcategory_slug || listing.subcategory_id) && (
                        <>
                            <span> / </span>
                            <Link 
                                to={`/category/${listing.category_slug || listing.category_id}/${listing.subcategory_slug || listing.subcategory_id}`}
                                className="hover:text-primary transition-colors"
                            >
                                {listing.subcategory_name}
                            </Link>
                        </>
                    )}
                </p>

                {/* Title - clickable */}
                <Link to={listingUrl} className="block">
                    <h3 className="font-heading font-semibold text-foreground line-clamp-3 sm:line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                        {listing.title}
                    </h3>
                </Link>

                {/* Spacer to push bottom content down */}
                <div className="flex-1"></div>

                {/* Bottom section - always aligned at bottom */}
                <div className="mt-auto">
                    {/* Mobile layout: Condition above action buttons */}
                    <div className="sm:hidden">
                        {/* Condition tag - centered */}
                        <div className="flex justify-center mb-2">
                            <Link to={listingUrl}>
                                <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs text-muted-foreground">
                                    {getConditionLabel(listing.condition)}
                                </span>
                            </Link>
                        </div>
                        
                        {/* Action buttons - centered */}
                        <div className="flex items-center justify-center gap-1 mb-3">
                            {/* Edit button - only for owner */}
                            {isOwner && (
                                <button
                                    onClick={handleEdit}
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-gray-100 transition-colors"
                                    title="Editează"
                                >
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                            )}
                            
                            {/* Report button - only for non-owners */}
                            {isAuthenticated && !isOwner && (
                                <button
                                    onClick={handleReport}
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                                    title="Raportează"
                                >
                                    <Flag className="w-3.5 h-3.5" />
                                </button>
                            )}
                            
                            {/* Share button */}
                            <button
                                onClick={handleShare}
                                className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors"
                                title="Distribuie"
                            >
                                <Share2 className="w-3.5 h-3.5" />
                            </button>
                            
                            {/* Like button */}
                            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                <LikeButton 
                                    listingId={listing.listing_id}
                                    initialLikesCount={listing.likes_count || 0}
                                    size="xs"
                                    showCount={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Desktop layout: Condition on its own row */}
                    <div className="hidden sm:flex items-center justify-between mb-3">
                        {/* Condition tag - left */}
                        <Link to={listingUrl} className="block">
                            <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs text-muted-foreground">
                                {getConditionLabel(listing.condition)}
                            </span>
                        </Link>
                    </div>

                        {/* Price - centered on mobile */}
                        <div className="sm:hidden text-center mb-2">
                            <Link to={listingUrl} className="block">
                                <p className="font-heading font-bold text-xl text-primary">
                                    {formatCurrency(listing.current_price)}
                                </p>
                                {isAuction && (
                                    <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                                        <Gavel className="w-3 h-3" />
                                        {listing.bid_count || 0} {(listing.bid_count || 0) === 1 ? 'ofertă' : 'oferte'}
                                    </p>
                                )}
                            </Link>
                        </div>

                        {/* Price and Action buttons row - desktop only */}
                        <div className="hidden sm:flex items-center justify-between">
                            {/* Price - left side */}
                            <Link to={listingUrl} className="block">
                                <p className="text-xs text-muted-foreground">
                                    {isAuction ? 'Preț curent' : 'Preț'}
                                </p>
                                <p className="font-heading font-bold text-xl text-foreground">
                                    {formatCurrency(listing.current_price)}
                                </p>
                                {isAuction && (
                                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Gavel className="w-3 h-3" />
                                        {listing.bid_count || 0} {(listing.bid_count || 0) === 1 ? 'ofertă' : 'oferte'}
                                    </p>
                                )}
                            </Link>

                            {/* Action buttons - desktop */}
                            <div className="flex items-center gap-1">
                                {/* Edit button - only for owner */}
                                {isOwner && (
                                    <button
                                        onClick={handleEdit}
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-gray-100 transition-colors"
                                        title="Editează"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                )}
                                
                                {/* Report button - only for non-owners */}
                                {isAuthenticated && !isOwner && (
                                    <button
                                        onClick={handleReport}
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                                        title="Raportează"
                                    >
                                        <Flag className="w-4 h-4" />
                                    </button>
                                )}
                                
                                {/* Share button */}
                                <button
                                    onClick={handleShare}
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors"
                                    title="Distribuie"
                                >
                                    <Share2 className="w-4 h-4" />
                                </button>
                                
                                {/* Like button */}
                                <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                                    <LikeButton 
                                        listingId={listing.listing_id}
                                        initialLikesCount={listing.likes_count || 0}
                                        size="xs"
                                        showCount={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Buy now price */}
                        {isAuction && listing.buy_now_price && (
                            <Link to={listingUrl}>
                                <p className="text-sm text-primary mt-2">
                                    Cumpără acum: {formatCurrency(listing.buy_now_price)}
                                </p>
                            </Link>
                        )}

                    {/* Seller info */}
                    <Link 
                        to={`/user/${listing.seller_username?.trim()}`}
                        className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50"
                    >
                        <SellerAvatar 
                            avatar={listing.seller_avatar}
                            name={listing.seller_name || listing.seller_username}
                        />
                        <span className="text-sm text-muted-foreground hover:text-foreground transition-colors truncate">
                            {listing.seller_name || listing.seller_username}
                        </span>
                        {listing.seller_rating > 0 && (
                            <span className="text-sm text-yellow-500 flex-shrink-0">★ {listing.seller_rating.toFixed(1)}</span>
                        )}
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
};

export default ListingCard;
